import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

interface Translations {
  ko: string | null;
  en: string | null;
}
export interface PropertyFaq {
  property_id: string;
  faq_sets: {
    collection_id: string;
    assignment_id: string;
    name: string | null;
    language: 'ko' | 'en' | 'und';
    collection_revision: number;
    assignment_revision: number;
    selection_mode: 'ALL' | 'EXPLICIT';
    items: {
      id: string;
      title: string | null;
      sort_order: number;
      revision: number;
      payload: {
        question: Translations;
        answer: Translations;
        detail: boolean;
        apply_completed: boolean;
        vacant_room: boolean;
        membership_state: 'CURRENT' | 'HISTORICAL';
        source_order: number | null;
      };
    }[];
  }[];
}
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown) => v === null || typeof v === 'string';
// Positive is int64 in Core; only exactly representable JSON integers are safe here.
const positive = (v: unknown) => Number.isSafeInteger(v) && Number(v) > 0;
const integer = (v: unknown, min: number, max: number) =>
  Number.isSafeInteger(v) && Number(v) >= min && Number(v) <= max;
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length && keys.every((key) => Object.hasOwn(v, key));
const member = (v: unknown, values: string[]) => typeof v === 'string' && values.includes(v);
// Core counts Unicode code points, rather than graphemes or UTF-16 units.
const faqText = (v: unknown) =>
  v === null || (typeof v === 'string' && Array.from(v).length <= 262144);
const translations = (v: unknown) =>
  record(v) && exact(v, ['ko', 'en']) && faqText(v.ko) && faqText(v.en);
// Core's ensure_ascii=False JSON budget includes Python's default separator spaces.
const textBudget = (question: unknown, answer: unknown) => {
  const json = JSON.stringify({ question, answer }).replace(/"(?:[^"\\]|\\.)*"|[:,]/g, (token) =>
    token === ':' || token === ',' ? token + ' ' : token,
  );
  return new TextEncoder().encode(json).length <= 1048576;
};
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: 'FAQ 응답을 확인하지 못했습니다.',
  });
/** Current assigned FAQ projection; no imported snapshot/source fallback. */
export async function readNativePropertyContent(
  client: ServiceClient,
  propertyId: string,
  signal?: AbortSignal,
): Promise<PropertyFaq> {
  if (!isUuid(propertyId)) throw invalid();
  const v = await client.get<unknown>(`admin/properties/${propertyId}/content`, { signal });
  if (
    !record(v) ||
    !exact(v, ['property_id', 'faq_sets']) ||
    typeof v.property_id !== 'string' ||
    v.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    !Array.isArray(v.faq_sets) ||
    !v.faq_sets.every(
      (set) =>
        record(set) &&
        exact(set, [
          'collection_id',
          'assignment_id',
          'name',
          'language',
          'collection_revision',
          'assignment_revision',
          'selection_mode',
          'items',
        ]) &&
        isUuid(set.collection_id) &&
        isUuid(set.assignment_id) &&
        text(set.name) &&
        member(set.language, ['ko', 'en', 'und']) &&
        positive(set.collection_revision) &&
        positive(set.assignment_revision) &&
        member(set.selection_mode, ['ALL', 'EXPLICIT']) &&
        Array.isArray(set.items) &&
        set.items.every(
          (item) =>
            record(item) &&
            exact(item, ['id', 'title', 'sort_order', 'revision', 'payload']) &&
            isUuid(item.id) &&
            text(item.title) &&
            integer(item.sort_order, 0, 2147483647) &&
            positive(item.revision) &&
            record(item.payload) &&
            exact(item.payload, [
              'question',
              'answer',
              'detail',
              'apply_completed',
              'vacant_room',
              'membership_state',
              'source_order',
            ]) &&
            translations(item.payload.question) &&
            translations(item.payload.answer) &&
            textBudget(item.payload.question, item.payload.answer) &&
            ['detail', 'apply_completed', 'vacant_room'].every(
              (flag) => typeof (item.payload as Record<string, unknown>)[flag] === 'boolean',
            ) &&
            member(item.payload.membership_state, ['CURRENT', 'HISTORICAL']) &&
            (item.payload.source_order === null ||
              integer(item.payload.source_order, -2147483648, 2147483647)),
        ),
    )
  )
    throw invalid();
  return v as unknown as PropertyFaq;
}
